// =============================================================================
// Usuarios — quem entra no Fênix e com qual perfil.
//
// ⚠️ Não se CRIA usuário aqui. Quem pode receber acesso é lido ao vivo do
//    parque: o usuário de portal (`dbo.Usuarios`, entra pelo login) e o
//    vendedor (`VENDEDOCAD`, entra pelo código). O que se administra é o
//    VÍNCULO: qual perfil a pessoa tem. Criar gente por fora faria existir login que a TI não conhece e que
//    ninguém desliga quando a pessoa sai da empresa.
// =============================================================================

import { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import api, { mensagemDeErro } from '../services/api';

export default function Usuarios() {
  const [usuarios, setUsuarios] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [busca, setBusca] = useState('');
  const [filtro, setFiltro] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const params = { busca: busca.trim() || undefined };
      if (filtro === 'sem') params.sem_perfil = 1;
      else if (filtro === 'ativos') params.ativos = 1;
      else if (filtro) params.perfil_id = filtro;

      const [u, p] = await Promise.all([
        api.get('/api/admin/usuarios', { params }),
        api.get('/api/admin/perfis'),
      ]);
      setUsuarios(u.data.usuarios);
      setResumo(u.data.resumo);
      setPerfis(p.data.perfis);
      setErro('');
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setCarregando(false);
    }
  }, [busca, filtro]);

  useEffect(() => {
    const t = setTimeout(carregar, 300);
    return () => clearTimeout(t);
  }, [carregar]);

  async function mudarPerfil(usuario, perfilId) {
    const antes = usuario.perfil_id;
    try {
      await api.put('/api/admin/usuarios/perfil', { chave: usuario.chave, perfil_id: perfilId || null });
      setUsuarios((atual) => atual.map((u) => (u.chave === usuario.chave
        ? { ...u, perfil_id: perfilId || null, perfil_nome: perfis.find((p) => String(p.id) === String(perfilId))?.nome || null }
        : u)));
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Não foi possível alterar', text: mensagemDeErro(e) });
      setUsuarios((atual) => atual.map((u) => (u.chave === usuario.chave ? { ...u, perfil_id: antes } : u)));
    }
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="h4 fw-bold mb-1">Usuários</h1>
          <p className="text-muted small mb-0">
            Usuários dos portais e vendedores do Microuniverso. Aqui você define o perfil de cada um — sem perfil, a pessoa não entra.
          </p>
        </div>
        {resumo && (
          <div className="text-muted small">
            <strong>{resumo.com_perfil}</strong> com acesso · {resumo.ativos} ativos · {resumo.total} no total
          </div>
        )}
      </div>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white"><i className="bi bi-search" /></span>
                <input className="form-control" placeholder="Buscar por nome, login ou código do vendedor"
                       value={busca} onChange={(e) => setBusca(e.target.value)} />
              </div>
            </div>
            <div className="col-12 col-md-6">
              <select className="form-select form-select-sm" value={filtro}
                      onChange={(e) => setFiltro(e.target.value)}>
                <option value="">Todos</option>
                <option value="ativos">Somente ativos</option>
                <option value="sem">Sem perfil (não entram)</option>
                {perfis.map((p) => <option key={p.id} value={p.id}>Perfil: {p.nome}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-fnx mb-0">
            <thead>
              <tr><th>Pessoa</th><th>Setor</th><th>Filial</th><th>Último acesso</th><th style={{ width: 210 }}>Perfil</th></tr>
            </thead>
            <tbody>
              {carregando && (
                <tr><td colSpan={5} className="text-center py-4 text-muted">
                  <span className="spinner-border spinner-border-sm me-2" />Carregando…
                </td></tr>
              )}
              {!carregando && usuarios.length === 0 && (
                <tr><td colSpan={5} className="text-center py-5 text-muted">Nenhum usuário encontrado.</td></tr>
              )}
              {usuarios.map((u) => (
                <tr key={u.chave} className={u.ativo ? '' : 'opacity-50'}>
                  <td>
                    <span className="fw-semibold">{u.nome || u.username}</span>
                    <div className="text-muted" style={{ fontSize: '.75rem' }}>
                      {u.origem === 'vendedor' ? `entra pelo código ${u.username}` : `login ${u.username}`}
                    </div>
                    {!u.ativo && <span className="badge badge-neutro ms-2">inativo</span>}
                    {u.codvend && <span className="badge badge-filial ms-2">vend. {u.codvend}</span>}
                  </td>
                  <td className="small text-muted">{u.setor || '—'}</td>
                  <td>{u.filial_padrao ? <span className="badge badge-filial">{u.filial_padrao}</span> : '—'}</td>
                  <td className="small text-muted">{u.ultimo_login_em
                    ? String(u.ultimo_login_em).slice(0, 16).replace(/^(\d{4})-(\d{2})-(\d{2})T/, '$3/$2/$1 ')   // hora local, lida como UTC pelo driver
                    : 'nunca'}</td>
                  <td>
                    <select className="form-select form-select-sm"
                            value={u.perfil_id || ''}
                            onChange={(e) => mudarPerfil(u, e.target.value)}>
                      <option value="">— sem acesso —</option>
                      {perfis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
